
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    Razorpay: any;
  }
}

export const useBooking = () => {
  const { toast } = useToast();
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentBookingId, setCurrentBookingId] = useState<string>("");
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load Razorpay script"));
      document.body.appendChild(script);
    });
  };

  const handleBooking = async (
    spotId: string,
    pricePerHour: number,
    date: Date,
    time: string,
    hours: string,
    vehicleNumber: string,
    lotCount: number = 1
  ) => {
    try {
      console.log("Starting booking process...", { spotId, date, time, hours, vehicleNumber, lotCount });
      
      await loadRazorpayScript();
      console.log("Razorpay script loaded successfully");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Parse hours to a float to handle half-hour durations
      const hoursFloat = parseFloat(hours);
      // Calculate amount based on hours (supports partial hours) and number of lots
      const amount = pricePerHour * hoursFloat * lotCount;

      const { data, error } = await supabase.functions.invoke('create-razorpay-order', {
        body: {
          parkingSpotId: spotId,
          hours: hoursFloat,
          date: date.toISOString().split('T')[0],
          time: time,
          vehicleNumber: vehicleNumber,
          amount: amount,
          userId: user.id,
          lotCount: lotCount
        }
      });

      if (error) {
        console.error("Error creating order:", error);
        throw error;
      }

      console.log("Order created successfully:", data);

      if (!data?.orderId || !data?.keyId) {
        throw new Error('Invalid response from payment service');
      }

      // Check if this is the user's first booking and a discount was applied
      if (data.isFirstBooking && data.discountApplied) {
        toast({
          title: "First-time User Discount!",
          description: "You've received a 10% discount on your first booking!",
          variant: "default",
        });
        
        // Show promo popup after successful first booking
        setTimeout(() => {
          setShowPromoPopup(true);
        }, 1000);
      }

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "Parking Spot Booking",
        description: `${hours} hour(s) parking booking for ${lotCount} ${lotCount === 1 ? 'lot' : 'lots'}`,
        order_id: data.orderId,
        handler: async function (response: any) {
          console.log("Payment successful:", response);
          
          // Update booking status to confirmed
          const { error: updateError } = await supabase
            .from('bookings')
            .update({ status: 'confirmed' })
            .eq('id', data.bookingId);

          if (updateError) {
            console.error("Error updating booking status:", updateError);
            toast({
              title: "Warning",
              description: "Payment successful but booking status update failed. Please contact support.",
              variant: "destructive",
            });
            return;
          }

          toast({
            title: "Payment Successful",
            description: "Your booking has been confirmed.",
          });
          setCurrentBookingId(data.bookingId);
          setShowFeedback(true);
        },
        modal: {
          ondismiss: async function() {
            console.log("Payment modal closed without completion");
            // Update booking status to failed if payment modal is closed
            const { error: updateError } = await supabase
              .from('bookings')
              .update({ status: 'failed' })
              .eq('id', data.bookingId);

            if (updateError) {
              console.error("Error updating booking status to failed:", updateError);
            }
          }
        },
        prefill: {
          name: user.user_metadata?.full_name || '',
          email: user.email || '',
          contact: user.user_metadata?.phone || ''
        },
        theme: {
          color: "#3B82F6"
        }
      };

      console.log("Initializing Razorpay with options:", options);
      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error: any) {
      console.error("Error in handleBooking:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to process payment. Please try again later.",
        variant: "destructive",
      });
    }
  };

  return { 
    handleBooking, 
    showFeedback, 
    setShowFeedback, 
    currentBookingId,
    showPromoPopup,
    setShowPromoPopup 
  };
};
